import { slugs } from '@/constants/slugs'
import { ContentEpisodeEdge } from '@/types/codegen-federation'
import { isARecap } from '@/utils/EpisodeUtil'
import { Episode, Season } from './types'

/** Input type for normalizeProjectSeasons - accepts any object with title.seasons structure */
export type ProjectWithTitleSeasons = {
  slug?: string | null
  projectType?: string | null
  title?: unknown // Use unknown to allow any title type, will check at runtime
}

/** Shape of the new content API season node */
interface TitleSeasonNode {
  id: string
  name?: string
  seasonNumber?: number
  episodes?: { edges?: ContentEpisodeEdge[] }
}

/** Shape of the new content API episode node we care about */
interface TitleEpisodeNode {
  id: string
  name?: string
  description?: string
  episodeNumber?: number
  posterCloudinaryPath?: string
  posterLandscapeCloudinaryPath?: string
  slug?: string
  subtitle?: string
  duration?: number
}

/** Type guard to check if an unknown value is a TitleSeasonNode */
function isTitleSeasonNode(value: unknown): value is TitleSeasonNode {
  return typeof value === 'object' && value !== null && 'id' in value && typeof value.id === 'string'
}

/** Type guard to check if an unknown value is a TitleEpisodeNode */
function isTitleEpisodeNode(value: unknown): value is TitleEpisodeNode {
  return typeof value === 'object' && value !== null && 'id' in value && typeof value.id === 'string'
}

/** Type guard to check if an unknown value has a seasons structure */
function hasSeasonsStructure(value: unknown): value is { seasons?: { edges?: Array<{ node?: unknown }> } } {
  return typeof value === 'object' && value !== null && 'seasons' in value && typeof value.seasons !== 'undefined'
}

function mapEpisodeNode(epNode: TitleEpisodeNode, season: TitleSeasonNode, project: ProjectWithTitleSeasons): Episode {
  const duration = epNode.duration ?? 0
  const episodeNumber = epNode.episodeNumber ?? 0
  const base: Episode = {
    description: epNode.description ?? '',
    episodeNumber,
    guid: epNode.id,
    id: epNode.id,
    name: epNode.name ?? '',
    posterCloudinaryPath: epNode.posterCloudinaryPath ?? '',
    posterLandscapeCloudinaryPath: epNode.posterLandscapeCloudinaryPath,
    projectSlug: project.slug ?? '',
    releaseDate: '',
    seasonId: season.id,
    seasonNumber: season.seasonNumber ?? 0,
    slug: epNode.slug ?? '',
    source: { duration, url: '' },
    subtitle: epNode.subtitle ?? '',
  }

  if ((!base.name || base.name.trim() === '') && base.subtitle) {
    base.name = base.subtitle
  }
  if (project.projectType === 'series' && episodeNumber > 0 && !isARecap({ name: epNode.name ?? '' })) {
    base.name = `Episode ${episodeNumber}`
  }
  if (isARecap({ name: epNode.name ?? '' }) && (!base.subtitle || base.subtitle.trim() === '')) {
    base.subtitle = base.name
  }
  return base
}

export function normalizeProjectSeasons<T extends ProjectWithTitleSeasons>(
  project: T | undefined,
): (T & { seasons: Season[] }) | undefined {
  if (!project) return undefined
  if (project.slug === slugs.dryBar) return { ...project, seasons: [] }

  // Check if title has the seasons structure we expect
  if (!hasSeasonsStructure(project.title)) {
    return { ...project, seasons: [] }
  }

  const titleSeasonsEdges = project.title.seasons?.edges

  // If the project doesn't have seasons data, return empty seasons array
  if (!titleSeasonsEdges || titleSeasonsEdges.length === 0) {
    return { ...project, seasons: [] }
  }

  const mappedSeasons: Season[] = titleSeasonsEdges
    .map((edge) => edge?.node)
    .filter(isTitleSeasonNode)
    .map((seasonNode) => {
      const rawEpisodeNodes = (seasonNode.episodes?.edges || []).map((epEdge) => epEdge?.node)
      const episodeNodes: TitleEpisodeNode[] = []
      for (const node of rawEpisodeNodes) {
        if (isTitleEpisodeNode(node)) {
          episodeNodes.push(node)
        }
      }
      const episodes: Episode[] = episodeNodes.map((epNode) => mapEpisodeNode(epNode, seasonNode, project))

      return {
        id: seasonNode.id,
        name: seasonNode.name ?? '',
        seasonNumber: seasonNode.seasonNumber,
        episodes,
      }
    })

  return { ...project, seasons: mappedSeasons }
}

/** Type representing a project after normalization with seasons data */
export type NormalizedProject<T extends ProjectWithTitleSeasons> = T & { seasons: Season[] }
